import { useEffect, useRef, useState, useMemo } from 'react'
import Map, { Marker, Source, Layer, NavigationControl } from 'react-map-gl'
import { Navigation } from 'lucide-react'
import { useApp } from '../../store/AppContext'
import { getDataSource, setDataSource, fetchLiveTracks, type DataSource, type LiveTrack } from '../../firebase/boatData'
import { gateFix, newGateState, haversineM, DEFAULT_GATE, type GateState } from '../../lib/positionGate'
import type { LineLayer } from 'react-map-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import maplibregl from 'maplibre-gl'

const LIVE_THRESHOLD = 120_000 // 2 minutes

// Trail continuity limits. A replay draws recorded fixes raw, so the line has to stop
// where our knowledge stops: 30 s of silence at a 1 Hz cadence is ~30 missing fixes, and
// 300 m between consecutive fixes is further than a boat covers between them at any sane
// speed. Both produce a BREAK, not a straight line across the unknown.
const TRAIL_MAX_GAP_S = 30
const TRAIL_MAX_STEP_M = 300

/** "LIVE" / "5 min ago" / "3 h ago" / "23 d ago" — the honest age of a boat's last fix. */
function agoText(ageMs: number): string {
    if (ageMs < LIVE_THRESHOLD) return 'LIVE'
    const m = Math.floor(ageMs / 60_000)
    if (m < 60) return `${m} min ago`
    const h = Math.floor(m / 60)
    if (h < 48) return `${h} h ago`
    return `${Math.floor(h / 24)} d ago`
}

// Web map-tile key — set VITE_MAPTILER_KEY (domain-restricted) in .env; literal
// is a dev fallback. Android needs a SEPARATE key (no HTTP referrer on native).
const MAPTILER_KEY = (import.meta as any).env.VITE_MAPTILER_KEY ?? '0NI7zu8a9ovaQXxNTigN'

// Distinct vivid colors for up to 10 boats — deterministically assigned by name hash
const BOAT_COLORS = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#22c55e', // green
    '#f59e0b', // amber
    '#a855f7', // purple
    '#ec4899', // pink
    '#14b8a6', // teal
    '#f97316', // orange
    '#6366f1', // indigo
    '#06b6d4', // cyan
]

function boatColor(boatId: string): string {
    let hash = 0
    for (let i = 0; i < boatId.length; i++) {
        hash = ((hash << 5) - hash + boatId.charCodeAt(i)) | 0
    }
    return BOAT_COLORS[Math.abs(hash) % BOAT_COLORS.length]
}

/**
 * What a track is, in words a coach can act on.
 *
 * The stored `algorithm_version` is the only honest signal, and it hides two very
 * different things behind one method name: `ppk-upload-v1` is a real carrier-phase solve
 * of the phone's raw satellite data, while `litpos-diff-v1` / `smooth-fb-v1` shift and
 * smooth the phone's own positions. The second is genuinely better than raw GPS and
 * genuinely NOT centimetre-accurate, and its sigma measures how smooth the curve is
 * rather than how right it is. Anything unrecognised is treated as the weaker case:
 * over-claiming accuracy is the expensive mistake.
 */
function trackQuality(algo?: string | null): { label: string; tip: string; phase: boolean } {
    const a = (algo || '').toLowerCase()
    const phase = !!a && !/diff|smooth/.test(a) && /(ppk|rtklib|carrier|phase)/.test(a)
    if (phase) return {
        label: 'Precise (centimetres)', phase: true,
        tip: "Recomputed from the phone's raw satellite data against a reference station. This is the accurate line — trust it for marks and distances.",
    }
    return {
        label: 'Improved (about a metre)', phase: false,
        tip: 'Smoothed and shifted using a nearby reference station. Better than the phone on its own, but not centimetre-accurate. Judge it by how tight the track looks, not by the small \u03c3 number.',
    }
}

// --- GPS provenance + achieved-accuracy badge -------------------------------------------------
// Tells the viewer WHAT they're looking at (phone GPS / raw / PPK / other recalced) and HOW good it
// is (scatter = RMS spread about the centroid, in metres; fix-rate for carrier-phase tracks). Reads
// the per-point fields the backend passes through: `corr` (litpos_only|local_reference|…), `src`
// (phone-gps|ppk|ekf|…), `kind`/`q` (fixed|float|single), `acc_m`.
type GpsInfo = { kind: string; detail: string; color: string; scatterM: number | null; n: number; fixPct: number | null; medAccM: number | null; sigmaM: number | null; isPhase: boolean }
function describeTrack(pts: Array<any>, source: string): GpsInfo {
    const recs = pts.filter(p => typeof p?.lat === 'number' && typeof p?.lon === 'number')
    const n = recs.length
    // kind: corrected overlay wins, else the per-point source tag, else the data-source default
    const anyCorr = recs.find(r => r.corr && r.corr !== 'none')?.corr
    const anySrc = recs.find(r => r.src)?.src
    // The ALGORITHM decides what may be claimed, not the method name. `litpos_only`
    // covers both a real carrier-phase solve and litpos-diff-v1 / smooth-fb-v1, which are
    // code-differential plus smoothing — and calling the second "PPK · carrier-phase" (as
    // this did) turns a metres-class track into a centimetre claim the data cannot
    // support. Unknown versions are treated as NOT carrier phase: over-claiming accuracy
    // is far more damaging than under-claiming it.
    // A loaded window usually MIXES algorithms: the carrier upgrade only replaces the spans
    // its raw files cover, so the rest stays smoothed. Grading such a track by whichever
    // point happened to come first is a coin toss, and half those tosses over-claim. Count
    // them instead and let the WEAKEST quality present decide the label — a track is only
    // "precise" if all of it is.
    const algos = recs.map(r => r.corr_algo).filter(Boolean).map(String)
    const phaseAlgo = algos.find(a => trackQuality(a).phase)
    const weakAlgo = algos.find(a => !trackQuality(a).phase)
    const phaseCount = algos.filter(a => trackQuality(a).phase).length
    // "All of it" is too strict in practice: the upgrade replaces the spans its raw files
    // cover, so a fully-solved run still carries a few smoothed points at the seams, and
    // calling a 99%-carrier track "about a metre" is its own kind of wrong. 95% is the line,
    // and whatever is left over is stated rather than hidden.
    const phasePctRaw = algos.length ? (100 * phaseCount) / algos.length : 0
    const isPhase = algos.length > 0 && phasePctRaw >= 95
    // How much of a mixed track IS carrier — worth saying, since it is the part to trust.
    const phasePct = Math.round(phasePctRaw)
    // Plain words first; the algorithm name stays as the small print, so the badge is
    // readable by a coach and still traceable by whoever has to debug it.
    let kind = 'Phone GPS', detail = 'straight from the handset', color = '#38bdf8'
    if (anyCorr) {
        // Name the algorithm that decided the verdict, so the small print matches the label.
        const shownAlgo = isPhase ? (phaseAlgo || weakAlgo) : (weakAlgo || phaseAlgo)
        const q = trackQuality(shownAlgo)
        kind = q.label
        detail = shownAlgo ? String(shownAlgo) : (anyCorr === 'local_reference' ? 'local reference' : String(anyCorr))
        if (phaseCount > 0 && phasePct < 100) detail += ` · ${phasePct}% precise`
        else if (phaseCount === 0 && algos.length) detail += ' · none of it carrier-solved'
        color = q.phase ? '#22c55e' : '#a3e635'
    }
    else if (anySrc === 'ppk') { kind = 'PPK'; detail = 'carrier-phase (per-epoch)'; color = '#22c55e' }
    else if (anySrc === 'ekf' || anySrc === 'fused') { kind = 'Fused'; detail = 'GNSS+IMU EKF'; color = '#818cf8' }
    else if (anySrc === 'phone-gps') { kind = 'Phone GPS'; detail = 'raw fused location'; color = '#38bdf8' }
    // The source toggle is a request, not evidence: with no correction fields on the loaded points
    // this is the phone's own GPS, whichever store it came from. Say which store.
    else if (source === 'server') { kind = 'Phone GPS'; detail = 'from our server — nothing recalculated for this range'; color = '#94a3b8' }
    // scatter = RMS distance to centroid (m)
    let scatterM: number | null = null
    if (n >= 2) {
        const la = recs.map(r => r.lat), lo = recs.map(r => r.lon)
        const cla = la.reduce((a, b) => a + b, 0) / n, clo = lo.reduce((a, b) => a + b, 0) / n
        const k = Math.cos(cla * Math.PI / 180)
        const ss = recs.reduce((s, r) => s + ((r.lat - cla) * 111320) ** 2 + ((r.lon - clo) * 111320 * k) ** 2, 0)
        scatterM = Math.sqrt(ss / n)
    }
    // fix-rate for carrier-phase tracks (q/kind present)
    const q = recs.filter(r => r.kind || typeof r.q === 'number')
    const fixPct = q.length ? Math.round(100 * q.filter(r => r.kind === 'fixed' || r.q === 1).length / q.length) : null
    // median per-fix reported accuracy — the honest ± number; "scatter" over a MOVING track is
    // just how far the boat travelled, not accuracy, so the card names the two separately.
    const accs = recs.map(r => (typeof r.acc_m === 'number' ? r.acc_m : (typeof r.acc === 'number' ? r.acc : null)))
        .filter((a): a is number => a != null && isFinite(a)).sort((a, b) => a - b)
    const medAccM = accs.length ? accs[Math.floor(accs.length / 2)] : null
    // The solver's own sigma, kept SEPARATE from accuracy. For the smoothed differential
    // path it is the residual against the solver's own curve — smoothness, not truth — so
    // it must never be printed as "accuracy". Shown, but named for what it is.
    const sigs = recs.map(r => (typeof r.sigma_m === 'number' ? r.sigma_m : null))
        .filter((a): a is number => a != null && isFinite(a)).sort((a, b) => a - b)
    const sigmaM = sigs.length ? sigs[Math.floor(sigs.length / 2)] : null
    return { kind, detail, color, scatterM, n, fixPct, medAccM, sigmaM, isPhase }
}

const trailLayer: LineLayer = {
    id: 'trail',
    type: 'line',
    paint: {
        'line-color': '#3b82f6',
        'line-width': 3,
        'line-opacity': 0.8
    }
}

// GNSS accuracy-state icon — matches the coach map badge (custom SVG, no emoji): a precision crosshair for
// centimetre, a looser dashed crosshair for decimetre, a dot-in-uncertainty-ring for raw phone GPS, a
// rotating arc while a correction is still coming.
function badgeState(g: GpsInfo): 'cm' | 'dm' | 'raw' {
    // Only a real carrier-phase solve may show the centimetre crosshair. The smoothed
    // differential path is corrected, but not to cm, and its icon said otherwise.
    if (g.isPhase) return (g.medAccM != null && g.medAccM < 0.5) ? 'cm' : 'dm'
    if (g.kind === 'Differential' || g.kind === 'Recalced') return 'dm'
    return 'raw'
}
function AccIcon({ state, size = 18 }: { state: string; size?: number }) {
    if (state === 'cm') return (
        <svg width={size} height={size} viewBox="0 0 26 26" fill="none" stroke="#4ade80" strokeLinecap="round">
            <circle cx="13" cy="13" r="6.4" strokeWidth="1.5" />
            <path d="M13 1.7V5.1M13 20.9v3.4M1.7 13H5.1M20.9 13h3.4" strokeWidth="1.5" />
            <circle cx="13" cy="13" r="1.9" fill="#4ade80" stroke="none" />
        </svg>)
    if (state === 'dm') return (
        <svg width={size} height={size} viewBox="0 0 26 26" fill="none" stroke="#fbbf24" strokeLinecap="round">
            <circle cx="13" cy="13" r="7.3" strokeWidth="1.5" strokeDasharray="2.7 2.4" />
            <path d="M13 2.1V5M13 21v2.9M2.1 13H5M21 13h2.9" strokeWidth="1.5" />
            <circle cx="13" cy="13" r="1.7" fill="#fbbf24" stroke="none" />
        </svg>)
    if (state === 'refining') return (
        <svg width={size} height={size} viewBox="0 0 26 26" fill="none" className="animate-spin">
            <circle cx="13" cy="13" r="8" stroke="#43e8d833" strokeWidth="2" />
            <path d="M13 5a8 8 0 0 1 7.4 4.5" stroke="#5fe6da" strokeWidth="2" strokeLinecap="round" />
        </svg>)
    return (
        <svg width={size} height={size} viewBox="0 0 26 26" fill="none">
            <circle cx="13" cy="13" r="8.6" stroke="#9fb4bd" strokeWidth="1.3" strokeDasharray="2.2 2.9" opacity="0.72" />
            <circle cx="13" cy="13" r="2.5" fill="#cfe0e6" />
        </svg>)
}

export default function BoatMap() {
    const { liveData, selectedBoats, playbackState, allBoatsLiveData, switchToLive, bumpSource, t } = useApp()
    const mapRef = useRef<any>(null)
    const [viewState, setViewState] = useState({
        longitude: 25.2797,
        latitude: 54.6872,
        zoom: 13
    })
    const [mapSensors, setMapSensors] = useState<string[]>(['vbat', 'wt', 'soc', 'wifiDbm']) // Default sensors
    // Debugging compare view: draw the pre-correction fused line and/or the phone's raw GPS line
    // UNDER the best-available trail, so what each accuracy step gains is visible on the same map.
    const [showFusedLayer, setShowFusedLayer] = useState(false)
    const [showRawLayer, setShowRawLayer] = useState(false)
    // Playback track layers (field request 2026-08-03): PPK and Phone GPS are LAYERS, toggled
    // without re-fetching — flipping them must not touch the playhead. Default = PPK shown.
    const [showPpkLayer, setShowPpkLayer] = useState(true)
    const [showPhoneLayer, setShowPhoneLayer] = useState(true)
    // Field overlays — the coach page's 📶/🧪 heatmaps, fleet/time-window flavoured:
    // live view covers the last 6 h; a loaded replay covers exactly its time span.
    // Trail shaping. A six-hour replay draws one flat line over itself and you cannot see
    // which way the boat went or when it was where. `trailMinutes` keeps only the last N
    // minutes before the playhead (0 = the whole track), and `trailFade` ramps the line
    // from transparent at its oldest end to solid at the boat, so direction and recency
    // read at a glance.
    const [trailMinutes, setTrailMinutes] = useState(0)
    const [trailFade, setTrailFade] = useState(true)
    const [showNetField, setShowNetField] = useState(false)
    const [showWaterField, setShowWaterField] = useState(false)
    const [waterParam, setWaterParam] = useState<'ph' | 'do' | 'wt' | 'tds' | 'turb'>('ph')
    const [netFC, setNetFC] = useState<any>(null)
    const [waterFC, setWaterFC] = useState<any>(null)

    // Position gate: reject GPS teleports and jumps outside their own reported
    // accuracy radius before a boat's marker moves — same protection as
    // sailgame.crowdbeam.eu's map. Accuracy is the phone's EKF-fused value
    // (GnssFusion.kt), parsed as `acc` in structuredToParsed().
    const gateStatesRef = useRef<Record<string, GateState>>({})
    const [stableById, setStableById] = useState<Record<string, { lat: number; lon: number }>>({})

    // Load sensor configs to potentially use for map (future: add specific 'showOnMap' flag)
    useEffect(() => {
        const saved = localStorage.getItem('sensor_configs')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                if (Array.isArray(parsed)) {
                    // For now, just take the first 4 visible sensors if we wanted dynamic
                    // But user asked for specific selection. We'll stick to defaults or top 4 visible for now.
                    const visible = parsed.filter(c => c.visible).map(c => c.key).slice(0, 4)
                    if (visible.length > 0) setMapSensors(visible)
                }
            } catch (e) {
                console.error('Failed to load sensor configs', e)
            }
        }
    }, [])

    // Center map on first playback frame when data is loaded
    useEffect(() => {
        if (playbackState.frames.length > 0) {
            const firstFrame = playbackState.frames[0]
            if (firstFrame?.parsed?.lat && firstFrame?.parsed?.lon) {
                const lat = Number(firstFrame.parsed.lat)
                const lon = Number(firstFrame.parsed.lon)
                if (!isNaN(lat) && !isNaN(lon)) {
                    setViewState(prev => ({
                        ...prev,
                        latitude: lat,
                        longitude: lon,
                        zoom: 15
                    }))
                }
            }
        }
    }, [playbackState.frames])

    // Helper to get value with fallbacks (matching Dashboard logic)
    function getValue(key: string, data: any) {
        if (!data) return undefined

        const fallbacks: Record<string, string[]> = {
            vbat: ['vbat', 'bat'],
            bat: ['phoneBat'],
            wt: ['wt', 't1'],
            temp: ['temp', 't0'],
            wifiDbm: ['wifiDbm', 'wifiLvl'],
            cellDbm: ['cellDbm', 'cellLvl']
        }

        const keys = fallbacks[key] || [key]
        for (const k of keys) {
            if (data[k] !== undefined && data[k] !== null) {
                return data[k]
            }
        }
        return undefined
    }

    // Get current positions based on mode (live or playback). Live markers render at
    // the gate-STABILISED position (last fix that passed the plausibility check), so
    // a rejected teleport leaves the boat where it was instead of snapping across the
    // map. Playback is already-recorded data, shown raw.
    // playbackActive = a timeline is LOADED (paused or playing) — a paused replay must stay a
    // replay: the marker freezes at the playhead and the trail stays. isPlaying only drives ticking.
    const playbackActive = playbackState.frames.length > 0

    // LIVE best-available track (owner, 2026-09-26: "the map must show best track available just like
    // go.tack.coach does — live solved PPK"): our server's live PPK / corrected line where solved, the phone's
    // fixes only for the newest seconds the live solve has not reached. Refreshed every 5 s while live.
    const [liveTracks, setLiveTracks] = useState<Record<string, LiveTrack>>({})
    useEffect(() => {
        if (playbackActive) { setLiveTracks({}); return }
        let alive = true
        const load = () => { fetchLiveTracks().then(t => { if (alive) setLiveTracks(t) }) }
        load()
        const id = setInterval(load, 5000)
        return () => { alive = false; clearInterval(id) }
    }, [playbackActive])
    const liveBestGeoJSON = useMemo(() => {
        if (playbackActive) return null
        const Q_COLOR: Record<string, string> = { p: '#22c55e', i: '#a3e635', g: '#38bdf8' }
        const features: any[] = []
        for (const [boatId, tr] of Object.entries(liveTracks)) {
            let run: [number, number][] = [], q = '', prev: [number, number, number, string] | null = null
            const flush = () => { if (run.length >= 2) features.push({ type: 'Feature', properties: { boatId, q, color: Q_COLOR[q] ?? '#38bdf8' }, geometry: { type: 'LineString', coordinates: run } }) }
            for (const pt of tr.pts) {
                const [ts, lon, lat, pq] = pt
                // same continuity rule as the replay trail: a long silence or an impossible jump starts a new line
                const broken = prev != null && (ts - prev[0] > 120_000 ||
                    haversineM(prev[2], prev[1], lat, lon) > Math.max(500, 45 * (ts - prev[0]) / 1000))
                if (broken) { flush(); run = [] }
                else if (pq !== q && run.length) { flush(); run = [run[run.length - 1]] }   // share the joint: no gap
                run.push([lon, lat]); q = pq; prev = pt
            }
            flush()
        }
        return features.length ? { type: 'FeatureCollection', features } : null
    }, [liveTracks, playbackActive])

    const rawPositions = playbackActive
        ? getPlaybackPositions()
        : getLivePositions()
    const currentPositions = playbackActive
        ? rawPositions
        : rawPositions.map(p => {
            const s = stableById[p.boatId]
            return s ? { ...p, lat: s.lat, lon: s.lon } : p
        })

    // Run every raw live fix through the position gate; stabilised position is what
    // actually renders. The single place a live boat's on-map position advances.
    const rawKey = rawPositions.map(p => `${p.boatId}:${p.lat},${p.lon}:${p.data?.time ?? ''}`).join('|')
    useEffect(() => {
        if (playbackActive || !rawPositions.length) return
        const newStable: Record<string, { lat: number; lon: number }> = {}
        for (const p of rawPositions) {
            const acc = typeof p.data?.acc === 'number' ? p.data.acc : undefined
            const fix = { lat: p.lat, lon: p.lon, ts: liveData.get(p.boatId)?.timestamp ?? Date.now(), acc }
            const prevState = gateStatesRef.current[p.boatId] ?? newGateState()
            const { state, outcome } = gateFix(prevState, fix)
            gateStatesRef.current[p.boatId] = state
            const anchor = outcome.accept ? fix : (state.last ?? fix)
            newStable[p.boatId] = { lat: anchor.lat, lon: anchor.lon }
        }
        setStableById(prev => ({ ...prev, ...newStable }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawKey])

    // Field overlays: fetch from the positioning API (same engine the coach page uses),
    // windowed to the replay span when one is loaded, else the last 6 hours of live data.
    // The window is quantised to a minute so a ticking playhead doesn't refetch per frame.
    const FIELDS_API = 'https://api.tack.coach'
    const fieldTo = playbackActive && playbackState.endTime ? playbackState.endTime : Date.now()
    const fieldFrom = playbackActive && playbackState.startTime ? playbackState.startTime : fieldTo - 6 * 3600_000
    const fieldKey = `${Math.floor(fieldFrom / 60000)}|${Math.floor(fieldTo / 60000)}`
    useEffect(() => {
        if (!showNetField) { setNetFC(null); return }
        let gone = false
        fetch(`${FIELDS_API}/v1/fields/net?from=${fieldFrom}&to=${fieldTo}`)
            .then(r => r.json()).then(d => { if (!gone) setNetFC(d) }).catch(() => { if (!gone) setNetFC(null) })
        return () => { gone = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showNetField, fieldKey])
    useEffect(() => {
        if (!showWaterField) { setWaterFC(null); return }
        let gone = false
        fetch(`${FIELDS_API}/v1/fields/water?param=${waterParam}&from=${fieldFrom}&to=${fieldTo}`)
            .then(r => r.json()).then(d => { if (!gone) setWaterFC(d) }).catch(() => { if (!gone) setWaterFC(null) })
        return () => { gone = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showWaterField, waterParam, fieldKey])

    // What's on screen + how accurate it is. Prefer the loaded replay track (full scatter); else live points.
    const gpsInfo = useMemo(() => {
        // Describe what is DRAWN, not merely what was loaded. With the corrected layer on,
        // the badge must describe that line — otherwise the map shows one track and the
        // badge grades another, which is how a smoothed line got read as centimetre-grade.
        const ppkPool = showPpkLayer && playbackState.ppkByBoat
            ? (Object.values(playbackState.ppkByBoat).flat() as any[]).map(f => f?.parsed).filter(Boolean)
            : []
        const pool = ppkPool.length > 0
            ? ppkPool
            : playbackState.frames.length > 0
                ? playbackState.frames.map((f: any) => f.parsed).filter(Boolean)
                : currentPositions.map(p => p.data)
        return describeTrack(pool, getDataSource())
    }, [playbackState.frames, playbackState.ppkByBoat, showPpkLayer, currentPositions])

    function getLivePositions() {
        const positions: Array<{ boatId: string, lat: number, lon: number, data: any }> = []
        selectedBoats.forEach(boatId => {
            const data = liveData.get(boatId)
            if (data?.parsed?.lat && data?.parsed?.lon) {
                positions.push({
                    boatId,
                    lat: data.parsed.lat,
                    lon: data.parsed.lon,
                    data: data.parsed
                })
            }
        })
        return positions
    }

    function getPlaybackPositions() {
        const { frames, currentIndex, framesByBoat, ppkByBoat, tCur } = playbackState
        // Fleet replay: every boat at its own last-known position at the playhead time.
        if (framesByBoat && tCur != null) {
            const out: Array<{ boatId: string, lat: number, lon: number, data: any }> = []
            // The marker follows the PPK layer when it is shown and has data for this boat —
            // "default PPK" — else the phone-GPS frames.
            const layerFrames = (boatId: string, fr: any[]) =>
                (showPpkLayer && ppkByBoat?.[boatId]?.length) ? ppkByBoat[boatId] : fr
            for (const [boatId, fr0] of Object.entries(framesByBoat)) {
                const fr = layerFrames(boatId, fr0 as any[])
                let last: any = null
                for (const f of fr) { if (f.timestamp <= tCur) last = f; else break }
                if (last?.parsed?.lat && last?.parsed?.lon)
                    out.push({ boatId, lat: last.parsed.lat, lon: last.parsed.lon, data: last.parsed })
            }
            if (out.length) return out
        }
        if (!frames[currentIndex]) return []

        const frame = frames[currentIndex]
        if (frame.parsed?.lat && frame.parsed?.lon) {
            return [{
                boatId: frame.boatId,
                lat: frame.parsed.lat,
                lon: frame.parsed.lon,
                data: frame.parsed
            }]
        }
        return []
    }

    // Generate trail from playback frames — stays visible while PAUSED too.
    // Fleet replay: one line per boat, coloured like its marker, drawn up to the playhead.
    const trailCutoff = trailMinutes > 0 && playbackState.tCur != null
        ? (playbackState.tCur as number) - trailMinutes * 60_000
        : -Infinity

    /**
     * Frames -> line segments, BROKEN wherever the boat could not physically have gone.
     *
     * Replay draws recorded fixes raw, and a phone that teleports (measured: 1.3 km in
     * 1.1 s on a Note 10, worst case 18 km) turns the trail into spokes radiating off the
     * map — the real track becomes unreadable, and a fading trail is pointless if the
     * thing being faded is mostly artefact. Same 45 m/s ceiling the live position gate
     * uses, so the map is consistent about what it considers possible. The points are
     * kept; only the LINE between them is cut, which is the honest rendering: we know the
     * boat was at both places, not that it travelled between them.
     */
    function trailSegments(fr: any[], cutoff: number, upTo: number): [number, number][][] {
        const out: [number, number][][] = []
        let run: [number, number][] = []
        let prev: { lat: number; lon: number; ts: number } | null = null
        for (const f of fr) {
            if (f.timestamp > upTo || f.timestamp < cutoff) continue
            const la = f.parsed?.lat, lo = f.parsed?.lon
            if (la == null || lo == null) continue
            if (prev) {
                const dtS = Math.max(0.001, (f.timestamp - prev.ts) / 1000)
                const d = haversineM(prev.lat, prev.lon, la, lo)
                // Three ways a straight line between two fixes would be a lie:
                //   · impossible speed — a teleport (the live gate's own ceiling);
                //   · a long silence — the boat went somewhere during those minutes and we
                //     have no idea where, so drawing a chord invents a path;
                //   · a big leap that is "slow" only because the gap was long, which is the
                //     same lie wearing a disguise.
                if (d / dtS > DEFAULT_GATE.maxSpeedMps || dtS > TRAIL_MAX_GAP_S || d > TRAIL_MAX_STEP_M) {
                    if (run.length >= 2) out.push(run)
                    run = []
                }
            }
            run.push([lo, la])
            prev = { lat: la, lon: lo, ts: f.timestamp }
        }
        if (run.length >= 2) out.push(run)
        return out
    }
    const trailGeoJSON = playbackActive && showPhoneLayer ? (
        playbackState.framesByBoat && playbackState.tCur != null ? {
            type: 'FeatureCollection' as const,
            features: Object.entries(playbackState.framesByBoat).flatMap(([boatId, fr]) =>
                trailSegments(fr, trailCutoff, playbackState.tCur as number).map(coords => ({
                    type: 'Feature' as const,
                    properties: { color: boatColor(boatId) },
                    geometry: { type: 'LineString' as const, coordinates: coords },
                })))
        } : {
        // Single-boat replay (no fleet map): same teleport-aware segmentation.
        type: 'FeatureCollection' as const,
        features: trailSegments(
            playbackState.frames.slice(0, playbackState.currentIndex + 1),
            trailCutoff,
            playbackState.frames[playbackState.currentIndex]?.timestamp ?? Infinity,
        ).map(coords => ({
            type: 'Feature' as const,
            properties: { color: boatColor(playbackState.frames[0]?.boatId || '') },
            geometry: { type: 'LineString' as const, coordinates: coords },
        })),
    }) : null

    // PPK layer — the server-recalced track per boat, drawn to the playhead with the same
    // teleport-aware segmentation. Pure rendering: toggling never re-fetches or moves the playhead.
    const ppkAvailable = playbackActive && playbackState.ppkByBoat != null
        && Object.values(playbackState.ppkByBoat).some(fr => (fr as any[]).length >= 2)
    // What the corrected line actually is. Read from the layer's own points, so a session
    // that was carrier-solved and one that was only smoothed never wear the same label.
    const ppkQuality = (() => {
        if (!ppkAvailable) return trackQuality(null)
        // Same rule the badge uses: a line is only "precise" when ~all of it is carrier —
        // the first point's algorithm is a coin toss on a mixed track.
        const algos = (Object.values(playbackState.ppkByBoat!).flat() as any[])
            .map(f => f?.parsed?.corr_algo).filter(Boolean).map(String)
        if (!algos.length) return trackQuality(null)
        const phase = algos.filter(a => trackQuality(a).phase)
        return trackQuality((phase.length / algos.length) >= 0.95 ? phase[0] : (algos.find(a => !trackQuality(a).phase) || phase[0]))
    })()
    const ppkGeoJSON = ppkAvailable && showPpkLayer && playbackState.tCur != null ? {
        type: 'FeatureCollection' as const,
        features: Object.entries(playbackState.ppkByBoat!).flatMap(([, fr]) =>
            trailSegments(fr as any[], trailCutoff, playbackState.tCur as number).map(coords => ({
                type: 'Feature' as const,
                properties: {},
                geometry: { type: 'LineString' as const, coordinates: coords },
            })))
    } : null

    // Accuracy-layer lines for the compare view. Fused = per-frame pre-correction position
    // (pre_lat where a corrected overlay replaced it, else lat); only offered when the trail
    // actually has corrected points, otherwise it would duplicate the main line. Raw = the
    // phone's unfused GPS (raw_lat/lon), published natively by the app.
    const playFrames = playbackActive ? playbackState.frames.slice(0, playbackState.currentIndex + 1) : []
    const hasCorrPts = playFrames.some(f => f.parsed?.pre_lat != null && f.parsed?.pre_lon != null)
    const fusedCoords = hasCorrPts
        ? playFrames.filter(f => (f.parsed?.pre_lat ?? f.parsed?.lat) != null && (f.parsed?.pre_lon ?? f.parsed?.lon) != null)
            .map(f => [f.parsed!.pre_lon ?? f.parsed!.lon!, f.parsed!.pre_lat ?? f.parsed!.lat!])
        : []
    const rawCoords = playFrames.filter(f => f.parsed?.raw_lat != null && f.parsed?.raw_lon != null)
        .map(f => [f.parsed!.raw_lon!, f.parsed!.raw_lat!])
    const fusedGeoJSON = showFusedLayer && fusedCoords.length >= 2
        ? { type: 'Feature' as const, geometry: { type: 'LineString' as const, coordinates: fusedCoords } } : null
    const rawGeoJSON = showRawLayer && rawCoords.length >= 2
        ? { type: 'Feature' as const, geometry: { type: 'LineString' as const, coordinates: rawCoords } } : null

    // Auto-center on first boat
    useEffect(() => {
        if (currentPositions.length > 0 && mapRef.current) {
            const first = currentPositions[0]
            mapRef.current.flyTo({
                center: [first.lon, first.lat],
                zoom: 15,
                duration: 1000
            })
        }
    }, [selectedBoats.join(',')])

    return (
        <div className="absolute inset-0">
            {/* Data source: Firebase (phone-GPS), own server (recalced/Timescale), or auto (freshest).
                Bottom-left — clear of the boat chooser (top-left), dashboard (top-right) and playback
                (bottom-centre). z-20 so it sits above those panels. Switches WITHOUT a page reload
                (triggerRefresh re-subscribes) so the session/login is preserved. */}
            <div className="absolute bottom-4 left-4 z-20 flex flex-col gap-2">
                {/* GPS provenance + achieved accuracy of what's currently shown. */}
                {gpsInfo.n > 0 && (
                    <div className="px-3 py-2 rounded-lg text-white bg-black/70 backdrop-blur-md border border-white/15 shadow-xl">
                        <div className="flex items-center gap-2 text-xs font-semibold">
                            <AccIcon state={badgeState(gpsInfo)} />
                            <span>{gpsInfo.kind}</span>
                            <span className="text-white/50 font-normal">· {gpsInfo.detail}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-white/70">
                            {/* Solver sigma for the NON-carrier-phase path, named honestly. Slow
                                multipath wander is smooth, passes the filter untouched and never
                                reaches sigma, so a small number here is not a small error. */}
                            {!gpsInfo.isPhase && gpsInfo.sigmaM != null && (
                                <span title="Residual against the solver's own smoothed curve — a measure of SMOOTHNESS, not of accuracy. Slow multipath wander is smooth and never appears in it. Judge the track by its spread, not by this.">
                                    solver σ <b className="text-white">{gpsInfo.sigmaM < 1 ? (gpsInfo.sigmaM * 100).toFixed(0) + ' cm' : gpsInfo.sigmaM.toFixed(2) + ' m'}</b>
                                    <span className="text-white/40"> (smoothness)</span>
                                </span>
                            )}
                            {gpsInfo.medAccM != null && (
                                <span title="Median per-fix reported accuracy (1σ) — how good each position is">accuracy <b className="text-white">±{gpsInfo.medAccM < 1 ? (gpsInfo.medAccM * 100).toFixed(0) + ' cm' : gpsInfo.medAccM.toFixed(1) + ' m'}</b></span>
                            )}
                            {gpsInfo.scatterM != null && (
                                <span title="RMS spread of the loaded points about their centroid — for a moving boat this is how far it travelled, NOT accuracy">track spread <b className="text-white">{gpsInfo.scatterM < 1 ? (gpsInfo.scatterM * 100).toFixed(0) + ' cm' : gpsInfo.scatterM.toFixed(1) + ' m'}</b></span>
                            )}
                            {gpsInfo.fixPct != null && <span>fixed <b className="text-white">{gpsInfo.fixPct}%</b></span>}
                            <span>{gpsInfo.n} pts</span>
                        </div>
                    </div>
                )}
                {/* Debugging compare: overlay the pre-correction fused line and the phone's raw GPS
                    line under the best track — what did each accuracy step buy us? Only shown in
                    playback and only for layers that actually differ from the main line. */}
                {playbackActive && (
                    <div className="px-3 py-2 rounded-lg text-white bg-black/70 backdrop-blur-md border border-white/15 shadow-xl text-[11px] space-y-1">
                        <div className="text-[9px] uppercase tracking-wider text-white/50">Track layers</div>
                        {/* Say what this line IS, where you switch it on. "PPK / recalced" told a
                            coach nothing about whether the line could be trusted for a mark. */}
                        <label className="flex items-center gap-2 cursor-pointer" title={ppkAvailable
                            ? ppkQuality.tip + ' The boat marker follows this line while it is on.'
                            : 'Nothing better than the phone track was recorded for this range.'}>
                            <input type="checkbox" checked={showPpkLayer} disabled={!ppkAvailable}
                                onChange={e => setShowPpkLayer(e.target.checked)} className="accent-green-400" />
                            <span className="inline-block w-4 h-1 rounded" style={{ background: ppkAvailable && ppkQuality.phase ? '#22c55e' : '#a3e635' }} />
                            <span className={ppkAvailable ? (ppkQuality.phase ? 'text-green-300' : 'text-lime-200') : 'text-white/40'}>
                                {ppkAvailable ? ppkQuality.label : 'Corrected track — none'}
                            </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer" title="The phone-reported track (sensors and CSV always come from here)">
                            <input type="checkbox" checked={showPhoneLayer}
                                onChange={e => setShowPhoneLayer(e.target.checked)} className="accent-sky-400" />
                            <span className="inline-block w-4 h-1 rounded" style={{ background: boatColor(playbackState.frames[0]?.boatId || '') }} />
                            <span>Phone GPS</span>
                        </label>
                        {hasCorrPts && (
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={showFusedLayer} onChange={e => setShowFusedLayer(e.target.checked)} className="accent-sky-400" />
                                <span className="inline-block w-4 h-1 rounded bg-sky-400" style={{ opacity: 0.85 }} />
                                <span className="text-sky-300">fused (pre-correction)</span>
                            </label>
                        )}
                        {rawCoords.length >= 2 && (
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={showRawLayer} onChange={e => setShowRawLayer(e.target.checked)} className="accent-gray-400" />
                                <span className="inline-block w-4 h-1 rounded bg-gray-400" style={{ opacity: 0.7 }} />
                                <span className="text-gray-300">phone raw GPS</span>
                            </label>
                        )}
                    </div>
                )}
                {/* Trail shaping — only meaningful while a replay is loaded. */}
                {playbackActive && (
                    <div>
                        <div className="text-[9px] uppercase tracking-wider text-white/50 mb-1 ml-0.5">Trail</div>
                        <div className="px-2 py-1.5 rounded-lg border border-white/15 shadow-xl backdrop-blur-md text-[11px]"
                            style={{ background: 'rgba(0,0,0,0.72)' }}>
                            <label className="flex items-center gap-2 cursor-pointer text-white/80">
                                <input type="checkbox" checked={trailFade} onChange={e => setTrailFade(e.target.checked)}
                                    className="accent-sky-400" />
                                <span title="Fade the line from transparent at its oldest end to solid at the boat, so direction and recency are visible">
                                    fade with age
                                </span>
                            </label>
                            <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="text-white/40 text-[10px]">length</span>
                                {([[0, 'all'], [5, '5m'], [15, '15m'], [60, '1h']] as const).map(([m, label]) => (
                                    <button key={m} onClick={() => setTrailMinutes(m)}
                                        title={m === 0 ? 'Draw the whole track' : `Keep only the last ${label} before the playhead`}
                                        className="px-1.5 py-0.5 rounded border border-white/15 text-[10px]"
                                        style={trailMinutes === m
                                            ? { background: '#43e8d8', color: '#06202b' }
                                            : { background: 'rgba(0,0,0,0.5)', color: '#fff' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Data-layer overlays — the coach page's heatmaps, windowed to the replay
                    when one is loaded, else the last 6 h of live data. */}
                <div>
                    <div className="text-[9px] uppercase tracking-wider text-white/50 mb-1 ml-0.5">Data layers</div>
                    <div className="flex flex-wrap gap-1 text-[11px] font-semibold">
                        <button onClick={() => setShowNetField(v => !v)}
                            title="Signal / reception heatmap — red where the mobile signal was weak. From the phones' real cell dBm, fix cadence, satellite C/N0 and accuracy."
                            className="px-2 py-1 rounded-lg border border-white/15 shadow-xl backdrop-blur-md"
                            style={{ background: showNetField ? '#43e8d8' : 'rgba(0,0,0,0.72)', color: showNetField ? '#06202b' : '#fff' }}>
                            📶 Signal{showNetField && netFC ? ` (${netFC.features?.length ?? 0})` : ''}
                        </button>
                        <button onClick={() => setShowWaterField(v => !v)}
                            title="Water chemistry heatmap — green where the chosen parameter sat in its healthy range, red where it did not. Needs a boat with the Arduino water sensors."
                            className="px-2 py-1 rounded-lg border border-white/15 shadow-xl backdrop-blur-md"
                            style={{ background: showWaterField ? '#2fd07e' : 'rgba(0,0,0,0.72)', color: showWaterField ? '#06202b' : '#fff' }}>
                            🧪 Water{showWaterField && waterFC ? ` (${waterFC.features?.length ?? 0})` : ''}
                        </button>
                        {/* Chemistry is sparse — a boat carries the probes on some days only — so the
                            server widens an empty window rather than returning nothing. Say so, or a
                            layer showing last week's readings looks like today's. */}
                        {showWaterField && waterFC?.fallback_used && (
                            <span className="px-2 py-1 rounded-lg text-[10px] border"
                                title="No readings in the current view, so the most recent ones (last 30 days) are shown instead"
                                style={{ background: 'rgba(234,179,8,0.15)', color: '#fde68a', borderColor: 'rgba(234,179,8,0.35)' }}>
                                showing latest available
                            </span>
                        )}
                        {showWaterField && waterFC && !waterFC.features?.length && (
                            <span className="px-2 py-1 rounded-lg text-[10px] border"
                                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.12)' }}>
                                no readings for this parameter
                            </span>
                        )}
                    </div>
                    {showWaterField && (
                        <div className="flex gap-1 mt-1 text-[10px] font-semibold">
                            {([['ph', 'pH'], ['do', 'O₂'], ['wt', '°C'], ['tds', 'TDS'], ['turb', 'Turb']] as const).map(([val, label]) => (
                                <button key={val} onClick={() => setWaterParam(val)}
                                    className="px-1.5 py-0.5 rounded border border-white/15"
                                    style={{ background: waterParam === val ? '#43e8d8' : 'rgba(0,0,0,0.6)', color: waterParam === val ? '#06202b' : '#fff' }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {/* Explicit, always-visible position-mode toggle — the ACTIVE mode is highlighted in its colour. */}
                <div>
                    <div className="text-[9px] uppercase tracking-wider text-white/50 mb-1 ml-0.5">Data from</div>
                    <div className="flex rounded-lg overflow-hidden border border-white/15 shadow-xl text-[11px] font-semibold">
                        {([['auto', 'Auto', 'server → Firebase', '#a78bfa'],
                           ['server', 'Our server', 'database', '#22c55e'],
                           ['firebase', 'Firebase', 'backup', '#38bdf8']] as const).map(([val, label, sub, col]) => {
                            const active = getDataSource() === val
                            return (
                                <button key={val}
                                    onClick={() => { setDataSource(val as DataSource); bumpSource() }}
                                    title={`Read boats, live positions, history and water data from ${label} (${sub})`}
                                    className="px-3 py-1.5 leading-tight transition-colors text-center"
                                    style={{ background: active ? col : 'rgba(0,0,0,0.72)', color: active ? '#06202b' : '#fff' }}>
                                    <div>{label}</div>
                                    <div className="text-[9px] opacity-70 font-normal">{sub}</div>
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>
            <Map
                ref={mapRef}
                {...viewState}
                onMove={evt => setViewState(evt.viewState)}
                mapLib={maplibregl as any}
                mapStyle={`https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}`}
                style={{ width: '100%', height: '100%' }}
            >
                <NavigationControl position="top-right" />

                {/* Field overlays under everything else — value-at-place circle fields,
                    same rendering treatment as the coach page's 📶/🧪 layers */}
                {showNetField && netFC && (
                    <Source id="netfield-src" type="geojson" data={netFC}>
                        <Layer id="netfield-dots" type="circle" paint={{
                            'circle-color': ['interpolate', ['linear'], ['get', 'q'],
                                0, '#1fb8c9', 0.2, '#5fd38a', 0.4, '#ffd93d', 0.6, '#ff8a3d', 0.85, '#ff3b3b'] as any,
                            'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 13, 7, 14, 11, 16, 20, 18, 38] as any,
                            'circle-blur': 0.8,
                            'circle-opacity': ['interpolate', ['linear'], ['get', 'q'], 0, 0.28, 0.34, 0.55, 1, 0.6] as any,
                        }} />
                    </Source>
                )}
                {showWaterField && waterFC && (
                    <Source id="waterfield-src" type="geojson" data={waterFC}>
                        <Layer id="waterfield-dots" type="circle" paint={{
                            'circle-color': ['interpolate', ['linear'], ['get', 'q'],
                                0, '#2fd07e', 0.35, '#ffd93d', 0.65, '#ff8a3d', 1, '#ff3b3b'] as any,
                            'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 5, 13, 9, 14, 14, 16, 24, 18, 42] as any,
                            'circle-blur': 0.75,
                            'circle-opacity': 0.6,
                        }} />
                    </Source>
                )}

                {/* Live: each boat's best-available track (green carrier-phase PPK, lime improved, blue phone GPS) */}
                {liveBestGeoJSON && (
                    <Source id="live-best-source" type="geojson" data={liveBestGeoJSON as any}>
                        <Layer id="live-best-trail" type="line" layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                            paint={{ 'line-color': ['get', 'color'] as any, 'line-width': 3, 'line-opacity': 0.9 }} />
                    </Source>
                )}

                {/* Trail in playback mode — alt accuracy layers first so the best line draws on top */}
                {rawGeoJSON && (
                    <Source id="raw-trail-source" type="geojson" data={rawGeoJSON as any}>
                        <Layer id="raw-trail" type="line" layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                            paint={{ 'line-color': '#9ca3af', 'line-width': 2, 'line-opacity': 0.7 }} />
                    </Source>
                )}
                {fusedGeoJSON && (
                    <Source id="fused-trail-source" type="geojson" data={fusedGeoJSON as any}>
                        <Layer id="fused-trail" type="line" layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                            paint={{ 'line-color': '#38bdf8', 'line-width': 2.5, 'line-opacity': 0.85, 'line-dasharray': [2, 1.5] }} />
                    </Source>
                )}
                {ppkGeoJSON && (
                    <Source id="ppk-trail-source" type="geojson" data={ppkGeoJSON as any}>
                        <Layer id="ppk-trail" type="line" layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                            paint={{ 'line-color': '#22c55e', 'line-width': 3, 'line-opacity': 0.9 }} />
                    </Source>
                )}
                {trailGeoJSON && (
                    // lineMetrics is what makes line-gradient possible: it gives MapLibre the
                    // along-line distance ('line-progress') the fade interpolates over.
                    <Source id="trail-source" type="geojson" data={trailGeoJSON as any} lineMetrics={trailFade}>
                        {/* Fleet replays carry a per-boat colour on each feature; single-boat replays fall back.
                            Fading uses a gradient rather than opacity because opacity is uniform per feature —
                            only a gradient can make the OLD end of one line fainter than its new end. A
                            gradient cannot read a per-feature property, so multi-boat fades share one hue and
                            the per-boat colours return the moment fading is off. */}
                        {trailFade ? (
                            <Layer {...trailLayer} paint={{
                                ...trailLayer.paint,
                                'line-opacity': 1,
                                'line-gradient': ['interpolate', ['linear'], ['line-progress'],
                                    0, 'rgba(59,130,246,0)', 0.35, 'rgba(59,130,246,0.35)', 1, '#60a5fa'] as any,
                            }} />
                        ) : (
                            <Layer {...trailLayer} paint={{ ...trailLayer.paint,
                                'line-color': ['coalesce', ['get', 'color'], boatColor(playbackState.frames[0]?.boatId || '')] as any }} />
                        )}
                    </Source>
                )}

                {/* Boat markers — stale boats (no fix within the live window) render grey with the
                    age on the label, so a 20-day-old "last known position" can't pass for live */}
                {currentPositions.map(pos => {
                    const ageMs = playbackActive ? 0 : Date.now() - (liveData.get(pos.boatId)?.timestamp ?? 0)
                    const stale = !playbackActive && ageMs > LIVE_THRESHOLD
                    return (
                    <Marker
                        key={pos.boatId}
                        longitude={pos.lon}
                        latitude={pos.lat}
                        anchor="center"
                    >
                        <div className={stale ? "relative group opacity-55 grayscale" : "relative group"}>
                            {/* Rotated Icon — color per boat */}
                            <div
                                className="w-12 h-12 rounded-full border-2 border-white shadow-lg flex items-center justify-center transition-transform duration-500 ease-linear"
                                style={{ backgroundColor: boatColor(pos.boatId), transform: `rotate(${pos.data.hea || 0}deg)` }}
                            >
                                <Navigation className="w-6 h-6 text-white fill-white" />
                            </div>

                            {/* Data Overlay */}
                            <div className="absolute left-14 top-0 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg p-2 min-w-[120px] text-xs text-white shadow-xl">
                                <div className="font-bold mb-1 border-b border-white/10 pb-1 flex items-center gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: boatColor(pos.boatId) }} />
                                    {pos.boatId}
                                    {stale && <span className="text-amber-300 font-normal">· {agoText(ageMs)}</span>}
                                    {!stale && pos.data?.acc != null && !isNaN(Number(pos.data.acc)) && (() => {
                                        const acc = Number(pos.data.acc)
                                        const col = acc < 0.3 ? '#2dd4bf' : acc < 2 ? '#22c55e' : acc < 10 ? '#fbbf24' : '#f87171'
                                        return <span className="font-mono font-bold text-[10px] px-1.5 rounded"
                                            title="Position accuracy (1σ)"
                                            style={{ color: col, background: `${col}18`, border: `1px solid ${col}33` }}>
                                            ±{acc < 1 ? acc.toFixed(2) : acc.toFixed(0)}m
                                        </span>
                                    })()}
                                </div>
                                {!stale && Number(pos.data?.acc) >= 40 && (
                                    <div className="mb-1 text-[10px] text-red-300">
                                        ⚠ suspect — Wi-Fi/cell fallback, may sit far from the boat
                                    </div>
                                )}
                                <div className="space-y-0.5">
                                    {mapSensors.map(key => {
                                        const val = getValue(key, pos.data)
                                        if (val === undefined) return null
                                        return (
                                            <div key={key} className="flex justify-between gap-2">
                                                <span className="text-white/60">{t(`sensors.${key}`)}</span>
                                                <span className="font-mono">{typeof val === 'number' ? val.toFixed(1) : val}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </Marker>
                    )
                })}

                {/* Live-now ghosts: every boat transmitting RIGHT NOW is on the map and clickable —
                    in live mode too, not only during playback (skip ones already shown as selected) */}
                {(() => {
                    const now = Date.now()
                    const ghosts: Array<{ boatId: string, lat: number, lon: number, hea: number }> = []
                    allBoatsLiveData.forEach((data, boatId) => {
                        const age = now - data.timestamp
                        if (!playbackActive && selectedBoats.includes(boatId)) return   // already a full marker
                        if (age < LIVE_THRESHOLD && data.parsed?.lat && data.parsed?.lon) {
                            ghosts.push({
                                boatId,
                                lat: Number(data.parsed.lat),
                                lon: Number(data.parsed.lon),
                                hea: Number(data.parsed.hea || 0)
                            })
                        }
                    })
                    return ghosts.map(g => (
                        <Marker
                            key={`ghost-${g.boatId}`}
                            longitude={g.lon}
                            latitude={g.lat}
                            anchor="center"
                        >
                            <button
                                onClick={() => switchToLive(g.boatId)}
                                className="relative group/ghost cursor-pointer"
                                title={`Watch ${g.boatId} live`}
                            >
                                {/* Pulsing ring */}
                                <span className="absolute inset-0 rounded-full animate-ping bg-green-400/40" />
                                {/* Inner dot */}
                                <div
                                    className="relative w-7 h-7 rounded-full border-2 border-green-400 bg-green-500/80 shadow-lg flex items-center justify-center transition-transform duration-500"
                                    style={{ transform: `rotate(${g.hea}deg)` }}
                                >
                                    <Navigation className="w-3.5 h-3.5 text-white fill-white" />
                                </div>
                                {/* Label — always visible and part of the click target */}
                                <div className="absolute left-9 top-0 bg-black/70 backdrop-blur-sm rounded px-2 py-1 text-[10px] text-green-300 font-mono whitespace-nowrap border border-green-500/30">
                                    🔴 {g.boatId}
                                </div>
                            </button>
                        </Marker>
                    ))
                })()}
            </Map>
        </div>
    )
}
